/**
 * OAuth Callback Handler - Simple Loading Component
 *
 * This component serves as a simple loading screen while the backend
 * handles the OAuth callback. The backend processes the authentication
 * and redirects appropriately - this component just provides visual feedback.
 *
 * NO VALIDATION happens here - the backend handles everything.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuthState } = useAuth();

  useEffect(() => {
    // Simple handler - just wait for backend to complete OAuth flow
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      console.log('Auth callback page loaded - backend is processing OAuth...');
      console.log('Callback URL:', window.location.href);
      console.log('Callback params:', {
        hasCode: !!code,
        hasState: !!state,
        error,
        errorDescription,
      });

      if (error) {
        console.error('OAuth callback returned error', {
          error,
          errorDescription,
        });
        return;
      }
      
      // Give backend time to complete the OAuth flow
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Check if we're authenticated now
      await checkAuthState();
      
      // Redirect to home - the app will handle routing based on auth state
      navigate('/');
    };

    handleCallback();
  }, [navigate, checkAuthState]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Completing sign in...
          </h2>
          <p className="mt-2 text-gray-600">
            Processing your authentication...
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Please wait a moment while we complete your sign in.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;